import React, { useCallback, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { TooltipWrapper } from './atoms'
import { Placement } from './tooltip'

interface Props {
  show: boolean
  content: React.ReactNode
  closeTimeout: React.MutableRefObject<number>
  handleClose: () => void
  containerWidth: number
  containerHeight: number
  containerTop: number
  containerLeft: number
  borderRadius: number
  placement: Placement
  background: string
  borderColor: string
  gap: number
  gapMb: number
}

export const TooltipElement = ({
  show,
  content,
  closeTimeout,
  handleClose,
  containerWidth,
  containerHeight,
  containerTop,
  containerLeft,
  placement,
  background,
  borderColor,
  borderRadius,
  gap,
  gapMb,
}: Props) => {
  const tooltipRef = useRef<HTMLDivElement | null>(null)
  const [top, setTop] = useState(0)
  const [left, setLeft] = useState<number | string>(0)

  const getPosition = useCallback(
    (width: number) => {
      let tempTop = 0
      let tempLeft: number | string = 0
      if (placement === 'bottom') {
        tempTop = containerTop + containerHeight
        tempLeft =
          width >= containerWidth
            ? containerLeft - (width - containerWidth) / 2
            : containerLeft + (containerWidth - width) / 2
      }
      if (placement === 'bottom-left') {
        tempTop = containerTop + containerHeight
        tempLeft = containerLeft
      }
      return { tempTop, tempLeft }
    },
    [containerHeight, containerLeft, containerTop, containerWidth, placement],
  )

  useEffect(() => {
    if (tooltipRef.current && show) {
      const { width } = tooltipRef.current.getBoundingClientRect()
      const { tempTop, tempLeft } = getPosition(width)
      setTop(tempTop)
      setLeft(tempLeft)
    }
  }, [getPosition, show])

  const tooltipElement = (
    <TooltipWrapper
      ref={tooltipRef}
      $background={background}
      $borderColor={borderColor}
      $placement={placement}
      $top={top}
      $left={left}
      $gap={gap}
      $gapMb={gapMb}
      $borderRadius={borderRadius}
      onMouseEnter={() => clearTimeout(closeTimeout.current)}
      onMouseLeave={() => handleClose()}
    >
      {content}
    </TooltipWrapper>
  )

  return show ? ReactDOM.createPortal(tooltipElement, document.body) : null
}
